import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Autoplay, Navigation, Scrollbar } from 'swiper/modules';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import {
  breakpointSizes,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  standardTransition,
  maxBreakpointQuery,
  visuallyHidden,
} from '../styles';
import { Container, Heading, Slider } from './ui';
import ProductCard from './ProductCard';

const StyledGrowsWellTogether = styled.section`
  ${sectionMargins()};
  overflow: hidden;
`;

const StyledInner = styled.div`
  position: relative;
`;

const StyledHeader = styled.header`
  margin-bottom: 30px;

  ${minBreakpointQuery.tiny`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.mlarge`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 60px;
    margin-bottom: 20px;
  `}
`;

const StyledButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  border: none;
  background: none;

  &:before {
    content: '';
    display: block;
    height: 12px;
    width: 12px;
    border-radius: 1px;
    border: 3px solid ${brandColours.primary};
    border-top: none;
    border-left: none;
    transition: ${standardTransition('border-color')};
    transform: ${({ prev }) =>
      prev
        ? `translateY(-50%) rotate(135deg)`
        : `translateY(-50%) rotate(-45deg)`};

    ${maxBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.mlarge`
      height: 16px;
      width: 16px;
    `}

    ${minBreakpointQuery.xlarge`
      height: 20px;
      width: 20px;
    `}
  }

  ${({ prev }) => {
    if (prev) {
      return css`
        left: -25px;

        ${minBreakpointQuery.mlarge`
          left: -30px;
        `}

        ${minBreakpointQuery.xlarge`
          left: -35px;
        `}

        ${minBreakpointQuery.xxxlarge`
          left: -40px;
        `}
      `;
    } else {
      return css`
        right: -25px;
      `;
    }
  }}
`;

const StyledSlider = styled(Slider)`
  --scrollbar-margin: 20px;
  overflow: visible;
  clip-path: inset(0px -100vw 0px 0px);
  padding-bottom: var(--scrollbar-margin);

  .swiper-container {
    width: 100%;
  }

  .swiper-slide {
    text-align: center;
    width: auto;
  }

  .swiper-wrapper {
    flex-direction: row !important;
  }

  ${minBreakpointQuery.small`
    --scrollbar-margin: 30px;
  `}

  ${minBreakpointQuery.smedium`
   --scrollbar-margin: 40px;
  `}

  ${minBreakpointQuery.medium`
    --scrollbar-margin: 50px;
  `}

  ${minBreakpointQuery.mlarge`
    --scrollbar-margin: 60px;
  `}

  ${minBreakpointQuery.large`
    --scrollbar-margin: 70px;
  `}

  ${minBreakpointQuery.xlarge`
    --scrollbar-margin: 80px;
  `}
`;

const StyledScrollbar = styled.div`
  margin-top: var(--scrollbar-margin);
  height: 4px;
  max-width: 100%;
  background-color: ${standardColours.lightGrey};
  --swiper-scrollbar-size: 4px;
  --swiper-scrollbar-drag-bg-color: ${brandColours.primary};
`;

const GrowsWellTogether = ({ items }) => {
  const nextEl = useRef(null);
  const prevEl = useRef(null);
  const scrollbarEl = useRef(null);

  const sliderOptions = {
    modules: [Autoplay, Navigation, Scrollbar],
    slidesPerView: 1,
    spaceBetween: 30,
    autoplay: {
      delay: 5000,
      pauseOnMouseEnter: true,
      disableOnInteraction: false,
    },
    speed: 1000,
    breakpoints: {
      [breakpointSizes.tiny]: { slidesPerView: 2 },
      [breakpointSizes.smedium]: { slidesPerView: 3 },
      [breakpointSizes.large]: { slidesPerView: 4 },
    },
    onInit: swiper => {
      swiper.params.navigation.prevEl = prevEl.current;
      swiper.params.navigation.nextEl = nextEl.current;
      swiper.navigation.init();
      swiper.navigation.update();
      swiper.params.scrollbar.el = scrollbarEl.current;
      swiper.params.scrollbar.draggable = true;
      swiper.scrollbar.init();
    },
  };

  return (
    <StyledGrowsWellTogether>
      <Container>
        <StyledInner>
          <StyledHeader>
            <Heading>Grows well together...</Heading>
          </StyledHeader>
          <StyledButton ref={prevEl} prev={true} aria-label="Previous" />
          <StyledButton ref={nextEl} aria-label="Next" />
          <StyledSlider {...sliderOptions}>
            {items.map(item => (
              <ProductCard key={item.id} data={item} />
            ))}
          </StyledSlider>
          <StyledScrollbar ref={scrollbarEl} />
        </StyledInner>
      </Container>
    </StyledGrowsWellTogether>
  );
};

export default GrowsWellTogether;
